import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import DashboardLayout from './components/layout/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import LeadsPage from './pages/LeadsPage';
import LeadDetailPage from './pages/LeadDetailPage';
import HypePage from './pages/HypePage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminPanel from './pages/AdminPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { tenantSlug } from './utils/tenant';

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent" />
    </div>
  );
}

// "/" — landing page for visitors; signed-in users go to their app
function Home() {
  const { isAuthenticated, isSuperAdmin, isLoading } = useAuth();
  if (isLoading) return <Loader />;
  // On a tenant subdomain (estaie.198x.ai) the root is the app, not the marketing site.
  if (!isAuthenticated) return tenantSlug() ? <Navigate to="/login" replace /> : <LandingPage />;
  return <Navigate to={isSuperAdmin ? '/admin' : '/dashboard'} replace />;
}

// Admin area — super-admin only
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin, isLoading } = useAuth();
  if (isLoading) return <Loader />;
  if (!isSuperAdmin) return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 5 * 60 * 1000 } },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <I18nProvider>
          <AuthProvider>
            <WebSocketProvider>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />

                {/* Company app */}
                <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<DashboardHome />} />
                  <Route path="/leads" element={<LeadsPage />} />
                  <Route path="/leads/:leadId" element={<LeadDetailPage />} />
                  <Route path="/hype" element={<HypePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster position="top-right" toastOptions={{
                duration: 4000,
                style: { background: '#1e293b', color: '#f1f5f9' },
                success: { iconTheme: { primary: '#8C2DE0', secondary: '#fff' } },
                error: { duration: 5000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
              }} />
            </WebSocketProvider>
          </AuthProvider>
        </I18nProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
